import { deleteData, postData } from "./common.service";
import imageCompression from 'browser-image-compression';

// Image compression options
const compressionOptions = {
    maxSizeMB: 0.8, // Maximum file size in MB (reduced from 2MB)
    maxWidthOrHeight: 1920, // Maximum width or height in pixels
    useWebWorker: true, // Use web worker for better performance
    fileType: 'image/jpeg', // Force JPEG format for better compression
    initialQuality: 0.7 // Reduced quality for better compression
};

export async function compressImage(file: File, customOptions?: Partial<typeof compressionOptions>): Promise<File> {
    try {
        console.log('Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        console.log('Original file type:', file.type);
        
        // Skip compression for very small files (< 100KB)
        if (file.size < 100 * 1024) {
            console.log('File is already small, skipping compression');
            return file;
        }
        
        // Merge custom options with default options
        const options = { ...compressionOptions, ...customOptions };
        
        // Adaptive compression based on file type and size
        if (file.type === 'image/png' && file.size > 500 * 1024) {
            // PNG files benefit more from JPEG conversion
            options.fileType = 'image/jpeg';
            options.initialQuality = 0.8;
        } else if (file.type === 'image/jpeg' && file.size < 500 * 1024) {
            // Small JPEG files, use lighter compression
            options.initialQuality = 0.85;
            // Keep original JPEG format for small files
            options.fileType = 'image/jpeg';
        }
        
        // Compress the image
        const compressedFile = await imageCompression(file, options);
        
        console.log('Compressed file size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
        console.log('Compressed file type:', compressedFile.type);
        
        // Check if compression actually reduced the file size
        if (compressedFile.size >= file.size) {
            console.log('Compression increased file size, using original file');
            return file;
        }
        
        const compressionRatio = ((1 - compressedFile.size / file.size) * 100);
        console.log('Compression ratio:', compressionRatio.toFixed(1) + '%');
        
        return compressedFile;
    } catch (error) {
        console.error('Error compressing image:', error);
        // If compression fails, return original file
        return file;
    }
}

export async function uploadFile(primaryId: string, secondaryId: string, file: File) {
    try {
        // Compress the image before uploading
        const compressedFile = await compressImage(file);
        
        const formdata = new FormData();
        formdata.append(compressedFile.name, compressedFile);
        
        return postData(`/images/${primaryId}/${secondaryId}/upload`, {
            basePath: process.env.REACT_APP_CORE_API_BASEPATH,
            sendTokenInHeader: true,
            headers: {
                'Accept': 'application/json'
            },
            body: formdata
        });
    } catch (error) {
        console.error('Error in uploadFile:', error);
        throw error;
    }
}

export async function deleteFile(imageUrl: string) {
    return deleteData(`/image?imageUrl=${imageUrl}`, {
        basePath: process.env.REACT_APP_CORE_API_BASEPATH,
        sendTokenInHeader: true,
        headers: {
            'Accept': 'application/json'
        }
    })
}
